/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { jsx, cssMap } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { Card } from '@atlaskit/smart-card/card/lazy';
import { FooterBlock } from '@atlaskit/smart-card/flexible/footer-block';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import { token } from '@atlaskit/tokens';

import type { SuggestionSource } from '../../../aiSuggestionsPluginType';

// Hide the smart card's own chrome so it sits cleanly inside our own container.
const cardUiOptions = {
	hidePadding: true,
	hideElevation: true,
	hideBackground: true,
	clickableContainer: true,
};

const styles = cssMap({
	sourcesContainer: {
		overflow: 'hidden',
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	sourceCard: {
		position: 'relative',
		boxSizing: 'border-box',
		overflow: 'hidden',
		width: '100%',
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		minHeight: '58px', // reserve space (single-line title + footer) to avoid layout shift while resolving
	},
	// Interactive affordance for the clickable source box.
	sourceCardInteractive: {
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	// Keep the card mounted (so its lazy IntersectionObserver still fires and it
	// resolves) but hidden, so the raw URL doesn't flash before it resolves.
	cardLoading: {
		visibility: 'hidden',
	},
	// Skeleton overlay shown while the card resolves. Absolutely positioned so it
	// sits on top of the (hidden) card without adding to the reserved height.
	skeletonOverlay: {
		position: 'absolute',
		inset: token('space.0'),
		boxSizing: 'border-box',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		gap: token('space.075'),
		pointerEvents: 'none',
	},
});

export type SuggestionCardSourcesProps = {
	sources: SuggestionSource[];
};

// Render nothing if rendering a source throws — most notably when this is mounted
// without an ancestor <SmartCardProvider> (e.g. outside the editor), where the smart
// Card throws. Hoisted so the reference is stable across renders.
const renderNoSources = (): null => null;

/** Skeleton stand-in for the card's title and footer while the URL resolves. */
function SourceSkeleton(): React.JSX.Element {
	return (
		<div css={styles.skeletonOverlay} aria-hidden={true}>
			<Skeleton width="65%" height={12} borderRadius={token('radius.small')} isShimmering />
			<Skeleton width="40%" height={10} borderRadius={token('radius.small')} isShimmering />
		</div>
	);
}

function SourceCard({ url }: { url: string }): React.JSX.Element | null {
	const [status, setStatus] = React.useState<'loading' | 'resolved' | 'errored'>('loading');
	const handleResolve = React.useCallback(() => setStatus('resolved'), []);
	const handleError = React.useCallback(() => setStatus('errored'), []);

	if (status === 'errored') {
		return null;
	}

	return (
		<>
			<div css={[status === 'loading' && styles.cardLoading]}>
				<Card
					appearance="block"
					ui={cardUiOptions}
					url={url}
					onResolve={handleResolve}
					onError={handleError}
				>
					<TitleBlock maxLines={1} hideIcon={true} anchorTarget="_blank" />
					<FooterBlock />
				</Card>
			</div>
			{status === 'loading' && <SourceSkeleton />}
		</>
	);
}

function SuggestionCardSourcesInner({
	sources,
}: SuggestionCardSourcesProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();

	// We only show the first source for now (multiple sources aren't supported yet).
	// The backend supplies the URL, so a source without one can't be linked to and is
	// dropped — even when it has an ARI.
	const url = sources[0]?.url;

	if (!url) {
		return null;
	}

	return (
		<div css={styles.sourcesContainer}>
			<Inline space="space.050" alignBlock="center">
				<TeamworkGraphIcon label="" size="small" color={token('color.icon.subtle')} />
				<Text as="span" color="color.text.subtle" size="small">
					{formatMessage(aiSuggestionsMessages.sourceLabel)}
				</Text>
			</Inline>
			<div css={[styles.sourceCard, styles.sourceCardInteractive]}>
				<SourceCard key={url} url={url} />
			</div>
		</div>
	);
}

export function SuggestionCardSources({ sources }: SuggestionCardSourcesProps): React.JSX.Element {
	return (
		<ErrorBoundary fallbackRender={renderNoSources}>
			<SuggestionCardSourcesInner sources={sources} />
		</ErrorBoundary>
	);
}
